import { useSiteInfo } from '@/SiteInfo/hooks/use-site-info';
import { SEO } from '@/components/SEO';
import { IconArrowRight, IconBulb, IconCircleCheck, IconHeart, IconHeartHandshake, IconPlant2, IconTrophy, IconUsers } from '@tabler/icons-react';

// Company values data
const values = [
  {
    icon: IconHeartHandshake,
    title: 'Transparent pricing',
    description: 'Everything is priced at our cost, with just 10% added at checkout. Honest prices every day—no gimmicks.',
  },
  {
    icon: IconHeart,
    title: 'Family owned & independent',
    description: 'We proudly carry on a tradition of family-owned, independent grocery stores that spans more than 50 years.',
  },
  {
    icon: IconPlant2,
    title: 'Fresh & "Triple Inspected"',
    description: 'A huge selection of fresh-cut meat and produce we "Triple Inspect" so only the best makes it to your cart.',
  },
  {
    icon: IconTrophy,
    title: 'Quality, guaranteed',
    description: 'At Good’s Cash Saver, quality and low prices are guaranteed—that’s our promise to every family we serve.',
  },
  {
    icon: IconUsers,
    title: 'Brands you love',
    description: 'A full variety of national brands alongside our own private-label products, all under one roof.',
  },
  {
    icon: IconBulb,
    title: 'Low prices, low expenses',
    description: 'We keep our prices low by keeping our expenses low, passing the savings straight on to you.',
  },
];

// Timeline milestones data - this would ideally come from an API
const milestones = [
  {
    date: 'For 50+ Years',
    title: 'A Family Tradition',
    description: 'Good’s Cash Saver proudly carries on a tradition of family-owned, independent grocery stores spanning more than five decades.',
  },
  {
    date: '2017',
    title: 'Transparent Pricing',
    description: 'We introduced Emporia to a transparent pricing model—everything priced at our cost, with just 10% added at checkout.',
  },
  {
    date: 'Every Day',
    title: 'Quality You Can Trust',
    description: 'A huge selection of fresh-cut meat, our “Triple Inspected” produce, and a full variety of national brands and private-label products.',
  },
  {
    date: 'Our Promise',
    title: 'Low Prices, Guaranteed',
    description: 'We keep our prices low by keeping our expenses low. At Good’s, quality and low prices are always guaranteed.',
  },
];

const About = () => {
  const { data: siteInfo } = useSiteInfo();
  const careersUrl = import.meta.env.VITE_CAREERS_URL;

  const siteName = siteInfo?.SiteTitle || 'Good’s Cash Saver';

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="About Us"
        description={`Learn about ${siteName} - our story, values, and commitment to serving our community with fresh, quality groceries.`}
      />

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
                Carrying on a family tradition for over 50 years
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {siteName} is proud to carry on a tradition of family-owned, independent grocery stores for more than 50 years. In 2017, we introduced Emporia to a transparent pricing model, where everything is priced at our cost with just 10% added at checkout.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mt-4">
                We offer a full variety of national brands and private-label products, a huge selection of fresh-cut meat, and our “Triple Inspected” produce. We keep our prices low by keeping our expenses low—so quality and low prices are always guaranteed. From our family to yours, remember to Get it at Good’s.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-muted">
                <img
                  src="https://assets.argretailers.com/media/goodscashsaver/about/web_crop_careers_002_a3e62a4363_bcafd57475.jpeg?w=800&h=600&fit=crop"
                  alt="Our team serving customers"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 bg-background border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0"></span>
                  <span className="text-sm font-medium text-primary">{milestone.date}</span>
                  {/* Connecting line to next item - hidden on last item and mobile */}
                  {index < milestones.length - 1 && (
                    <div className="hidden lg:block flex-1 h-px bg-neutral-200"></div>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {milestone.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {milestone.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-2xl mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Our values
            </h2>
            <p className="text-lg text-muted-foreground">
              These core principles guide everything we do—from how we price our products to how we serve every family that walks through our doors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <value.icon className="h-5 w-5 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">
                    {value.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-16 bg-background border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-square rounded-xl overflow-hidden bg-muted">
                  <img
                    src="https://images.unsplash.com/photo-1542838132-92c53300491e?w=400&h=400&fit=crop"
                    alt="Fresh produce"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-xl overflow-hidden bg-muted mt-8">
                  <img
                    src="https://images.unsplash.com/photo-1607349913338-fca6f7fc42d0?w=400&h=400&fit=crop"
                    alt="Local bakery"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-xl overflow-hidden bg-muted">
                  <img
                    src="https://images.unsplash.com/photo-1534723452862-4c874018d66d?w=400&h=400&fit=crop"
                    alt="Community support"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-xl overflow-hidden bg-muted mt-8">
                  <img
                    src="https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=400&h=400&fit=crop"
                    alt="Quality meats"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Committed to our community
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                We're more than just a grocery store—we're your neighbors. Good’s Cash Saver has been part of the Emporia community for generations, and we're honored to serve the families who shop with us.
              </p>
              <p className="text-lg text-muted-foreground">
                From a full variety of national brands and private-label products to fresh-cut meat and “Triple Inspected” produce, we work every day to bring you quality you can trust at prices you can count on. From our family to yours, remember to Get it at Good’s.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Join Our Team Section */}
      <section className="py-16 md:py-24 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-muted">
              <img
                src="https://assets.argretailers.com/media/goodscashsaver/about/web_crop_careers_001_a2e19b5de7_27cb13af34.jpeg?w=800&h=600&fit=crop"
                alt="Our team at work"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Join our team
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                We're always looking for passionate people to join our growing team. Enjoy great benefits and be part of something special.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <IconCircleCheck className="h-5 w-5 text-primary" />
                  <span className="text-foreground">Competitive pay</span>
                </div>
                <div className="flex items-center gap-3">
                  <IconCircleCheck className="h-5 w-5 text-primary" />
                  <span className="text-foreground">Flexible scheduling</span>
                </div>
                <div className="flex items-center gap-3">
                  <IconCircleCheck className="h-5 w-5 text-primary" />
                  <span className="text-foreground">Health benefits</span>
                </div>
                <div className="flex items-center gap-3">
                  <IconCircleCheck className="h-5 w-5 text-primary" />
                  <span className="text-foreground">Career growth</span>
                </div>
                <div className="flex items-center gap-3">
                  <IconCircleCheck className="h-5 w-5 text-primary" />
                  <span className="text-foreground">Employee discounts</span>
                </div>
                <div className="flex items-center gap-3">
                  <IconCircleCheck className="h-5 w-5 text-primary" />
                  <span className="text-foreground">Great work environment</span>
                </div>
              </div>
              {careersUrl && (
                <a
                  href={careersUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
                >
                  See our job postings
                  <IconArrowRight className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;